// components/groupAdmin/feed/course/CourseBuilderDialog.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowUp, ArrowDown, Upload, Pencil } from 'lucide-react';

import { adminFeedApi } from '../feedApi';

/**
 * Fixes:
 * 1) Video button now always opens file picker (no label/hidden-input quirks)
 * 2) Lessons/modules render even if backend uses different keys (name/items/videos)
 * 3) Upload uses course-specific backend route if available, otherwise falls back to /groups/:groupId/feed/upload-video
 * 4) Optimistic UI for add module/lesson so you see them immediately
 */
export default function CourseBuilderDialog({ open, onOpenChange, groupId, courseId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [priceInput, setPriceInput] = useState('');

  // File picker plumbing (robust inside dialogs)
  const videoInputRef = useRef(null);
  const [videoTarget, setVideoTarget] = useState(null); // { moduleId, lessonId }

  const showErr = (fallback, e) => {
    const detail = e?.response?.data?.detail;
    toast.error((typeof detail === 'string' && detail.trim()) ? detail : fallback);
  };

  const isFree = (c) => c?.is_free !== false;

  const toCents = (raw) => {
    const cleaned = String(raw ?? '').trim().replace(',', '.');
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  };

  const normalizeLesson = (l, idx = 0) => {
    if (!l) return null;
    return {
      ...l,
      id: l.id || l.lesson_id || l.lessonId || l._id || `lesson_${idx}_${Date.now()}`,
      title: l.title || l.name || l.label || `Lesson ${idx + 1}`,
      video_url: l.video_url || l.videoUrl || l.video?.url || null,
      video_path: l.video_path || l.videoPath || l.video?.path || null,
    };
  };

  const normalizeModule = (m, idx = 0) => {
    if (!m) return null;
    const rawLessons = Array.isArray(m.lessons)
      ? m.lessons
      : Array.isArray(m.items)
      ? m.items
      : Array.isArray(m.videos)
      ? m.videos
      : [];

    return {
      ...m,
      id: m.id || m.module_id || m.moduleId || m._id || `module_${idx}_${Date.now()}`,
      title: m.title || m.name || m.label || `Module ${idx + 1}`,
      lessons: rawLessons.map((x, i) => normalizeLesson(x, i)).filter(Boolean),
    };
  };

  const normalizeBuilder = (data) => {
    const c = data?.course || data?.data?.course || data?.builder?.course || null;
    const rawModules =
      data?.modules ||
      data?.data?.modules ||
      data?.builder?.modules ||
      c?.modules ||
      c?.outline ||
      [];
    const mods = (Array.isArray(rawModules) ? rawModules : []).map((m, i) => normalizeModule(m, i)).filter(Boolean);
    return { course: c ? { ...c, is_free: isFree(c) } : null, modules: mods };
  };

  const load = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const data = await adminFeedApi.getCourseBuilder(groupId, courseId);
      const norm = normalizeBuilder(data);
      setCourse(norm.course);
      setModules(norm.modules);

      const cents = Number(norm.course?.price_cents ?? norm.course?.priceCents ?? 0);
      setPriceInput(cents > 0 ? String((cents / 100).toFixed(2)) : '');
    } catch (e) {
      console.error(e);
      showErr('Course builder endpoint is missing or failing.', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId, groupId]);

  const counts = useMemo(() => {
    const lessonCount = (modules || []).reduce((acc, m) => acc + ((m.lessons || []).length), 0);
    return { moduleCount: modules?.length || 0, lessonCount };
  }, [modules]);

  const saveCourseMeta = async () => {
    if (!course?.title?.trim()) return toast.error('Course title required');

    const free = isFree(course);
    const cents = free ? 0 : toCents(priceInput);

    if (!free && cents <= 0) {
      return toast.error('Enter a valid price (e.g. 9.99) or mark course as Free.');
    }

    try {
      await adminFeedApi.updateCourse(groupId, courseId, {
        title: course.title.trim(),
        description: (course.description || '').trim(),
        status: course.status || 'draft',
        is_free: free,
        price_cents: cents,
        currency: (course.currency || 'usd').toLowerCase(),
      });
      toast.success('Course saved');
      onSaved?.();
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to save course', e);
    }
  };

  // ---------- Optimistic module/lesson ops ----------
  const addModule = async () => {
    const title = prompt('Module title?');
    if (!title?.trim()) return;

    const tempId = `tmp_mod_${Date.now()}`;
    setModules((prev) => [...(prev || []), { id: tempId, title: title.trim(), lessons: [] }]);

    try {
      await adminFeedApi.addModule(groupId, courseId, { title: title.trim(), name: title.trim() });
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to add module (backend route missing). Added locally for now.', e);
    }
  };

  const renameModule = async (moduleId, current) => {
    const title = prompt('New module title:', current || '');
    if (!title?.trim()) return;

    setModules((prev) => (prev || []).map((m) => (m.id === moduleId ? { ...m, title: title.trim() } : m)));

    try {
      await adminFeedApi.updateModule(groupId, courseId, moduleId, { title: title.trim(), name: title.trim() });
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to rename module', e);
    }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module?')) return;

    setModules((prev) => (prev || []).filter((m) => m.id !== moduleId));

    try {
      await adminFeedApi.deleteModule(groupId, courseId, moduleId);
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to delete module', e);
    }
  };

  const moveModule = async (fromIdx, dir) => {
    const next = [...(modules || [])];
    const toIdx = fromIdx + dir;
    if (toIdx < 0 || toIdx >= next.length) return;
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    setModules(next);

    try {
      await adminFeedApi.reorderModules(groupId, courseId, next.map((x) => x.id));
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to reorder modules', e);
    }
  };

  const addLesson = async (moduleId) => {
    const title = prompt('Lesson title?');
    if (!title?.trim()) return;

    const tempId = `tmp_lesson_${Date.now()}`;
    setModules((prev) =>
      (prev || []).map((m) =>
        m.id === moduleId ? { ...m, lessons: [...(m.lessons || []), { id: tempId, title: title.trim() }] } : m
      )
    );

    try {
      await adminFeedApi.addLesson(groupId, courseId, moduleId, { title: title.trim(), name: title.trim() });
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to add lesson (backend route missing). Added locally for now.', e);
    }
  };

  const renameLesson = async (moduleId, lessonId, current) => {
    const title = prompt('New lesson title:', current || '');
    if (!title?.trim()) return;

    setModules((prev) =>
      (prev || []).map((m) =>
        m.id === moduleId
          ? { ...m, lessons: (m.lessons || []).map((l) => (l.id === lessonId ? { ...l, title: title.trim() } : l)) }
          : m
      )
    );

    try {
      await adminFeedApi.updateLesson(groupId, courseId, moduleId, lessonId, { title: title.trim(), name: title.trim() });
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to rename lesson', e);
    }
  };

  const deleteLesson = async (moduleId, lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;

    setModules((prev) =>
      (prev || []).map((m) =>
        m.id === moduleId ? { ...m, lessons: (m.lessons || []).filter((l) => l.id !== lessonId) } : m
      )
    );

    try {
      await adminFeedApi.deleteLesson(groupId, courseId, moduleId, lessonId);
      await load();
    } catch (e) {
      console.error(e);
      showErr('Failed to delete lesson', e);
    }
  };

  const clickUpload = (moduleId, lessonId) => {
    setVideoTarget({ moduleId, lessonId });
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
      videoInputRef.current.click();
    }
  };

  const doUpload = async (file) => {
    if (!file || !videoTarget) return;
    const { moduleId, lessonId } = videoTarget;

    try {
      toast.message('Uploading video...');

      // Uses lesson-specific route if available, otherwise falls back to /feed/upload-video.
      await adminFeedApi.uploadLessonVideo(groupId, courseId, moduleId, lessonId, file);

      toast.success('Video uploaded');
      setVideoTarget(null);
      await load();
    } catch (e) {
      console.error(e);
      showErr('Video upload failed', e);
    }
  };

  const onVideoPicked = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    await doUpload(f);
  };

  const lessonCountText = `${counts.moduleCount} modules - ${counts.lessonCount} lessons`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Course Builder</DialogTitle>
          <DialogDescription>
            Edit course info, modules, lessons and upload videos.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          onChange={onVideoPicked}
          className="hidden"
        />

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading...</div>
        ) : !course ? (
          <div className="py-10 text-center text-sm text-slate-500">No course found.</div>
        ) : (
          <div className="grid md:grid-cols-[360px,1fr] gap-4">
            {/* Left: Meta */}
            <div className="space-y-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{course.title || 'Untitled'}</CardTitle>
                  <CardDescription className="text-xs">
                    <Badge variant="outline" className="mr-2">
                      {course.status || 'draft'}
                    </Badge>
                    <span className="text-slate-500">{lessonCountText}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Title</label>
                    <Input
                      value={course.title || ''}
                      onChange={(e) => setCourse((c) => ({ ...c, title: e.target.value }))}
                      placeholder="Course title"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-600">Description</label>
                    <Textarea
                      value={course.description || ''}
                      onChange={(e) => setCourse((c) => ({ ...c, description: e.target.value }))}
                      placeholder="What is this course about?"
                      rows={4}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-slate-600">Free course</p>
                      <p className="text-[11px] text-slate-500">If off, you must set a price</p>
                    </div>
                    <Switch
                      checked={isFree(course)}
                      onCheckedChange={(v) => setCourse((c) => ({ ...c, is_free: !!v }))}
                    />
                  </div>

                  {!isFree(course) && (
                    <div>
                      <label className="text-xs font-medium text-slate-600">Price (USD)</label>
                      <Input
                        value={priceInput}
                        onChange={(e) => setPriceInput(e.target.value)}
                        placeholder="9.99"
                        inputMode="decimal"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">Saved as cents: {toCents(priceInput)}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-slate-600">Status</label>
                    <div className="flex gap-2 mt-1">
                      {['draft', 'published'].map((s) => (
                        <Button
                          key={s}
                          type="button"
                          variant={String(course.status || 'draft') === s ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCourse((c) => ({ ...c, status: s }))}
                        >
                          {s}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={saveCourseMeta} className="w-full">
                Save course
              </Button>
            </div>

            {/* Right: Modules */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Modules</h3>
                <Button size="sm" onClick={addModule}>
                  <Plus size={16} className="mr-2" />
                  Add module
                </Button>
              </div>

              {(modules || []).length === 0 ? (
                <div className="border rounded-lg p-4 text-sm text-slate-500">No modules yet.</div>
              ) : (
                <div className="space-y-3">
                  {(modules || []).map((m, idx) => (
                    <Card key={m.id} className="border border-slate-200">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <CardTitle className="text-sm truncate">{m.title}</CardTitle>
                            <CardDescription className="text-xs">
                              {(m.lessons || []).length} lesson{(m.lessons || []).length === 1 ? '' : 's'}
                            </CardDescription>
                          </div>

                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="outline" onClick={() => moveModule(idx, -1)} title="Move up">
                              <ArrowUp size={14} />
                            </Button>
                            <Button size="icon" variant="outline" onClick={() => moveModule(idx, 1)} title="Move down">
                              <ArrowDown size={14} />
                            </Button>
                            <Button size="icon" variant="outline" onClick={() => renameModule(m.id, m.title)} title="Rename">
                              <Pencil size={14} />
                            </Button>
                            <Button size="icon" variant="destructive" onClick={() => deleteModule(m.id)} title="Delete">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-2">
                        <div className="flex justify-between items-center">
                          <p className="text-xs font-medium text-slate-600">Lessons</p>
                          <Button size="sm" variant="outline" onClick={() => addLesson(m.id)}>
                            <Plus size={14} className="mr-2" />
                            Add lesson
                          </Button>
                        </div>

                        {(m.lessons || []).length === 0 ? (
                          <div className="text-sm text-slate-500">No lessons yet.</div>
                        ) : (
                          <div className="space-y-2">
                            {(m.lessons || []).map((l) => (
                              <div
                                key={l.id}
                                className="border rounded-lg px-3 py-2 flex items-center justify-between gap-2"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm truncate">{l.title}</p>
                                  <p className="text-[11px] text-slate-500 truncate">
                                    {l.video_url ? 'Video linked' : 'No video'}
                                  </p>
                                </div>

                                <div className="flex items-center gap-1">
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => renameLesson(m.id, l.id, l.title)}
                                    title="Rename"
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => clickUpload(m.id, l.id)}
                                    title="Upload video"
                                  >
                                    <Upload size={14} />
                                  </Button>
                                  <Button size="icon" variant="destructive" onClick={() => deleteLesson(m.id, l.id)} title="Delete">
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
